import { useState, useEffect, useRef, createContext, useCallback, useMemo } from "react";
import API from "../services/api";
import { getAccountSets, getCurrentUser, setAccountSetsCache } from "../services/sessionCache";
import { resolveAccountSetId, setStoredAccountSetId } from "../services/accountSetSelection";
import symbolsCache from "../services/symbolsCache";
import { useToast } from "../components/Toast";
import AppPageLoader from "../components/AppPageLoader";
import CustomSelect from "../components/CustomSelect";

import SellPremiumChart from "../components/SellPremiumChart";
import {
  connectWS,
  onMessage,
  subscribeToPremium,
  subscribeToQuotes,
  subscribeToOpenOrders,
  subscribeToOrderProfit,
  subscribeToPositions,
  isWSConnected,
  clearQuoteSubscriptions,
  wsManager,
  createBrokerKey,
  isQuoteFresh,
  onBalanceBatch,
  onQuoteBatch,
} from "../services/wsService";
import {
  TrendingUp,
  DollarSign,
  Activity,
  Lock,
  Unlock,
  WalletMinimal,
  PiggyBank,
  AlertTriangle,
} from "lucide-react";
import {
  toFiniteOrNull,
  mergeSwapValue,
  withComputedTradeFields,
  mergeTradeSnapshotMetrics,
  buildBrokerScopedLookup,
  findBrokerScopedPayload,
} from "../utils/liveTradeProfit";
import "./Dashboard.css";

const PROFIT_FRESH_MS = 5000;
const DISPLAY_PROFIT_THROTTLE_MS = 500;
const PENDING_ORDERS_FALLBACK_REFRESH_MS = 15000;
export const DashboardContext = createContext();

function normalizeDashboardTrade(trade) {
  return withComputedTradeFields({
    ...trade,
    broker1Profit: trade?.broker1Profit ?? null,
    broker2Profit: trade?.broker2Profit ?? null,
    broker1Swap: trade?.broker1Swap ?? null,
    broker2Swap: trade?.broker2Swap ?? null,
  });
}

function sumMapValues(map) {
  let total = 0;
  map.forEach((value) => {
    total += Number(value) || 0;
  });
  return total;
}

function resolveLiveUpdatePosition(update, trades = []) {
  const directPosition = Number(update?.brokerPosition);
  if (Number.isFinite(directPosition)) return directPosition;

  const brokerId = String(update?.brokerId ?? "").trim();
  const ticket = String(update?.ticket ?? update?.Ticket ?? "").trim();
  const symbol = String(update?.symbol ?? update?.Symbol ?? "").trim();
  let matchedPosition = null;

  for (const trade of trades) {
    const matchesBroker1Ticket =
      ticket &&
      String(trade?.broker1Ticket ?? "") === ticket &&
      (!brokerId || String(trade?.broker1Id ?? "") === brokerId);
    const matchesBroker2Ticket =
      ticket &&
      String(trade?.broker2Ticket ?? "") === ticket &&
      (!brokerId || String(trade?.broker2Id ?? "") === brokerId);

    if (matchesBroker1Ticket) {
      if (matchedPosition && matchedPosition !== 1) return null;
      matchedPosition = 1;
      continue;
    }

    if (matchesBroker2Ticket) {
      if (matchedPosition && matchedPosition !== 2) return null;
      matchedPosition = 2;
    }
  }

  if (matchedPosition) return matchedPosition;
  if (ticket) return null;

  for (const trade of trades) {
    const matchesBroker1Symbol =
      symbol &&
      String(trade?.broker1Symbol ?? "").trim() === symbol &&
      (!brokerId || String(trade?.broker1Id ?? "") === brokerId);
    const matchesBroker2Symbol =
      symbol &&
      String(trade?.broker2Symbol ?? "").trim() === symbol &&
      (!brokerId || String(trade?.broker2Id ?? "") === brokerId);

    if (matchesBroker1Symbol) {
      if (matchedPosition && matchedPosition !== 1) return null;
      matchedPosition = 1;
      continue;
    }

    if (matchesBroker2Symbol) {
      if (matchedPosition && matchedPosition !== 2) return null;
      matchedPosition = 2;
    }
  }

  return matchedPosition;
}

function resolveDashboardLinkStatus(set) {
  if (!set) return "";
  if (typeof set.linkStatus === "string" && set.linkStatus.trim()) {
    return set.linkStatus;
  }
  return set?.symbolsLocked || set?.futureSymbol || set?.spotSymbol ? "active" : "draft";
}

function getDashboardApprovalGateMeta(linkStatus) {
  switch (linkStatus) {
    case "pending_review":
      return {
        tone: "info",
        message: "Admin review is still pending for this account set.",
        actionMessage:
          "This account set is still pending admin review. Lock Symbols will unlock after approval and reconnect.",
      };
    case "reconnect_required":
      return {
        tone: "warning",
        message:
          "Approved changes are saved. Run Link & Connect in Link Account before locking symbols again.",
        actionMessage: "Run Link & Connect in Link Account before locking symbols again.",
      };
    case "draft":
    default:
      return {
        tone: "warning",
        message: "Submit this account set for review in Link Account before locking symbols.",
        actionMessage:
          "This account set must be reviewed and approved before symbols can be locked.",
      };
  }
}

export default function Dashboard() {
  const toast = useToast();
  const [user, setUser] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [, setTradingStatus] = useState("Loading...");
  const [accountSets, setAccountSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [selectedTimeframe, setSelectedTimeframe] = useState(1);

  const currentSet = useMemo(
    () => accountSets.find((s) => s._id === selectedSetId) || { brokers: [] },
    [accountSets, selectedSetId],
  );
  const currentLinkStatus = useMemo(() => resolveDashboardLinkStatus(currentSet), [currentSet]);
  const [brokerBalances, setBrokerBalances] = useState({});
  const [tradeMapping, setTradeMapping] = useState([]);
  const [activeTrades, setActiveTrades] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [closedTradesCount, setClosedTradesCount] = useState(null);
  const [closedTradesTotalProfit, setClosedTradesTotalProfit] = useState(null);
  const [futureQuote, setFutureQuote] = useState(null);
  const [spotQuote, setSpotQuote] = useState(null);
  const [buyPremium, setBuyPremium] = useState(0);
  const [sellPremium, setSellPremium] = useState(0);
  const [lockedFutureQuote, setLockedFutureQuote] = useState(null);
  const [lockedSpotQuote, setLockedSpotQuote] = useState(null);
  const [displayQuoteSourceLabel, setDisplayQuoteSourceLabel] = useState("");
  const [quoteFeedStatus, setQuoteFeedStatus] = useState("");
  const [broker1Symbols, setBroker1Symbols] = useState([]);
  const [broker2Symbols, setBroker2Symbols] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [futureSymbol, setFutureSymbol] = useState("");
  const [spotSymbol, setSpotSymbol] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const lastWsConnectionState = useRef(null);
  const wsStatusCheckInterval = useRef(null);
  const lastToastMetaRef = useRef({ message: "", at: 0 });
  const marketPauseToastShownRef = useRef(false);

  const brokers = useMemo(() => currentSet.brokers || [], [currentSet]);
  const canLockSymbols = currentLinkStatus === "active";
  const lockGateMeta =
    selectedSetId && !currentSet?.symbolsLocked && !canLockSymbols
      ? getDashboardApprovalGateMeta(currentLinkStatus)
      : null;
  const sortedBrokers = [...brokers].sort((a, b) => (a.position || 0) - (b.position || 0));
  const broker1 = sortedBrokers.find((b) => b.position === 1) || {};
  const broker2 = sortedBrokers.find((b) => b.position === 2) || {};
  const broker1Term = broker1.terminal || "";
  const broker2Term = broker2.terminal || "";
  const broker1Valid =
    broker1.server && broker1.accountNumber && broker1.position && broker1.terminal;
  const broker2Valid =
    broker2.server && broker2.accountNumber && broker2.position && broker2.terminal;
  const key1 = broker1Valid ? createBrokerKey(broker1) : "";
  const key2 = broker2Valid ? createBrokerKey(broker2) : "";

  const broker1Id = broker1.id || broker1._id;
  const broker2Id = broker2.id || broker2._id;
  const getKpiValueClass = (value) => {
    if (value > 0) return "card-summary-value card-summary-value-positive";
    if (value < 0) return "card-summary-value card-summary-value-negative";
    return "card-summary-value";
  };
  const getKpiDetailValueClass = (value, forcedTone = "") => {
    const base = "card-detail-value";
    if (forcedTone === "positive") return `${base} card-detail-value-positive`;
    if (forcedTone === "negative") return `${base} card-detail-value-negative`;
    if (value > 0) return `${base} card-detail-value-positive`;
    if (value < 0) return `${base} card-detail-value-negative`;
    return base;
  };
  const defaultBalance = {
    balance: 0,
    equity: 0,
    profit: 0,
    margin: 0,
    freeMargin: 0,
    marginLevel: 0,
    leverage: 0,
    lastUpdate: 0,
  };
  const b1 = key1 ? brokerBalances[key1] || defaultBalance : defaultBalance;
  const b2 = key2 ? brokerBalances[key2] || defaultBalance : defaultBalance;
  const DEBUG = process.env.REACT_APP_DEBUG_LOGS === "true";
  const debugDashboard = useCallback(
    (...args) => {
      if (DEBUG) {
        console.log(...args);
      }
    },
    [DEBUG],
  );
  const showDashboardToast = useCallback(
    (message, type = "error", duration = 4000) => {
      const nextMessage = String(message || "").trim();
      if (!nextMessage) return;

      const nowTs = Date.now();
      if (
        lastToastMetaRef.current.message === nextMessage &&
        nowTs - lastToastMetaRef.current.at < 3000
      ) {
        return;
      }

      lastToastMetaRef.current = { message: nextMessage, at: nowTs };
      const showToast = toast[type] || toast.error;
      showToast(nextMessage, duration);
    },
    [toast],
  );
  const showMarketPauseWarning = useCallback(() => {
    if (marketPauseToastShownRef.current) return;
    if (typeof window !== "undefined") {
      const lastAt = Number(window.__premiumProfitPassiveMarketPauseToastAt || 0);
      const nowTs = Date.now();
      if (nowTs - lastAt < 3000) {
        marketPauseToastShownRef.current = true;
        return;
      }
      window.__premiumProfitPassiveMarketPauseToastAt = nowTs;
    }
    marketPauseToastShownRef.current = true;
    showDashboardToast(
      "Live prices are paused right now. Please wait for fresh quotes.",
      "warning",
    );
  }, [showDashboardToast]);

  const normalizeQuoteSource = useCallback((source) => {
    const value = String(source || "").toLowerCase();
    if (!value) return "";
    if (value.includes("collector")) return "collector";
    if (value.includes("ws_quote") || value.includes("websocket") || value.startsWith("ws"))
      return "ws";
    if (value.includes("api") || value.includes("rest")) return "rest";
    if (
      value.includes("database") ||
      value.includes("ws-db") ||
      value.includes("db") ||
      value.includes("cache")
    )
      return "db";
    return value;
  }, []);
  const isCollectorAvailabilityNotice = (message) =>
    /collector (quotes|premium updates) unavailable/i.test(String(message || ""));
  const getCollectorAvailabilityLabel = (message) =>
    /no snapshot yet/i.test(String(message || "")) ? "No Snapshot Yet" : "Market Closed";
  const isCollectorWarningPayload = useCallback((payload) => {
    const source = String(payload?.source || payload?.data?.source || "").toLowerCase();
    const collectorStale =
      payload?.collectorStale === true || payload?.data?.collectorStale === true;
    const message = String(payload?.message || payload?.data?.message || "").toLowerCase();

    return (
      collectorStale ||
      source === "collector_stale" ||
      source === "collector_missing" ||
      message.includes("balance feed delayed") ||
      message.includes("balance feed not ready yet") ||
      message.includes("positions feed delayed") ||
      message.includes("positions feed not ready yet")
    );
  }, []);
  const isMarketPausePayload = useCallback((payload) => {
    const message = String(payload?.message || payload?.data?.message || "").toLowerCase();
    const reason = String(payload?.reason || payload?.data?.reason || "").toLowerCase();
    const marketClosed = payload?.marketClosed === true || payload?.data?.marketClosed === true;

    return (
      marketClosed ||
      reason === "market_closed" ||
      reason === "stale_quotes" ||
      message.includes("tp blocked: stale quotes") ||
      message.includes("pending order blocked: stale quotes") ||
      message.includes("pending order realtime update blocked: stale quotes") ||
      message.includes("pre-execution premium check failed: stale quotes") ||
      message.includes("quotes are too stale for safe execution") ||
      message.includes("please wait for fresh quotes") ||
      message.includes("market likely closed") ||
      message.includes("premium updates are paused")
    );
  }, []);

  const buildQuoteSourceLabel = useCallback(
    (future, spot) => {
      const futureSrc = normalizeQuoteSource(future?.source);
      const spotSrc = normalizeQuoteSource(spot?.source);
      if (!futureSrc && !spotSrc) return "";
      if (futureSrc && spotSrc && futureSrc === spotSrc) return futureSrc;
      if (futureSrc && spotSrc) return `future:${futureSrc} spot:${spotSrc}`;
      return futureSrc || spotSrc;
    },
    [normalizeQuoteSource],
  );
  const deriveQuoteSourceLabel = useCallback(
    (payload) =>
      buildQuoteSourceLabel(payload?.futureQuote, payload?.spotQuote) ||
      normalizeQuoteSource(
        payload?.futureQuote?.source || payload?.spotQuote?.source || payload?.source,
      ),
    [buildQuoteSourceLabel, normalizeQuoteSource],
  );
  const now = Date.now();
  const b1Fresh = b1.lastUpdate && now - b1.lastUpdate < 300000;
  const b2Fresh = b2.lastUpdate && now - b2.lastUpdate < 300000;
  if (DEBUG) {
    debugDashboard("Broker balance lookup debug:", {
      key1,
      key2,
      broker1Data: b1,
      broker2Data: b2,
      broker1Fresh: b1Fresh,
      broker2Fresh: b2Fresh,
      availableBalanceKeys: Object.keys(brokerBalances),
      balanceDataEntries: Object.entries(brokerBalances),
      wsManagerConnected: wsManager?.socket?.readyState === WebSocket.OPEN,
    });
  }
  const [realTimeProfits, setRealTimeProfits] = useState({
    broker1: 0,
    broker2: 0,
    total: 0,
  });
  const [openOrdersSnapshot, setOpenOrdersSnapshot] = useState([]);

  const filteredActiveTrades = activeTrades;

  const filteredOpenOrdersSnapshot = openOrdersSnapshot;

  const mapSymbolOptions = useCallback((symbols, selectedValue) => {
    const normalized = Array.isArray(symbols)
      ? symbols.map((sym) => {
          const value =
            typeof sym === "object"
              ? sym.currency || sym.symbol || sym.name || JSON.stringify(sym)
              : sym;
          return {
            value,
            label: value,
          };
        })
      : [];

    if (selectedValue && !normalized.some((option) => option.value === selectedValue)) {
      normalized.unshift({
        value: selectedValue,
        label: selectedValue,
      });
    }

    return normalized;
  }, []);
  const [displayProfits, setDisplayProfits] = useState({ broker1: 0, broker2: 0, total: 0 });
  const profitsRef = useRef(realTimeProfits);
  const activeTradesRef = useRef(activeTrades);
  const lastDisplayAtRef = useRef(0);
  useEffect(() => {
    profitsRef.current = realTimeProfits;
  }, [realTimeProfits]);
  useEffect(() => {
    activeTradesRef.current = activeTrades;
  }, [activeTrades]);
  const b1TicketProfitRef = useRef(new Map());
  const b2TicketProfitRef = useRef(new Map());
  const b1TicketSwapRef = useRef(new Map());
  const b2TicketSwapRef = useRef(new Map());
  const collectorProfitAtRef = useRef(0);
  const profitBatchAtRef = useRef(0);
  const lastCollectorQuoteAt = useRef(0);
  const COLLECTOR_PREFER_MS = 3000;
  useEffect(() => {
    const nowTs = Date.now();
    if (nowTs - (lastDisplayAtRef.current || 0) >= DISPLAY_PROFIT_THROTTLE_MS) {
      setDisplayProfits(profitsRef.current);
      lastDisplayAtRef.current = nowTs;
    }
  }, [realTimeProfits]);
  useEffect(() => {
    setDisplayProfits(profitsRef.current);
    lastDisplayAtRef.current = Date.now();
    const id = setInterval(() => {
      setDisplayProfits(profitsRef.current);
      lastDisplayAtRef.current = Date.now();
    }, DISPLAY_PROFIT_THROTTLE_MS);
    return () => clearInterval(id);
  }, [selectedSetId]);
  const broker1PremixProfit = filteredActiveTrades.reduce((sum, t) => {
    const profit = parseFloat(t.broker1Profit) || 0;
    const swap = parseFloat(t.broker1Swap) || 0;
    return sum + profit + swap;
  }, 0);

  const broker2PremixProfit = filteredActiveTrades.reduce((sum, t) => {
    const profit = parseFloat(t.broker2Profit) || 0;
    const swap = parseFloat(t.broker2Swap) || 0;
    return sum + profit + swap;
  }, 0);
  const wsOpenOrdersProfit = filteredActiveTrades.reduce(
    (sum, t) => sum + (parseFloat(t.totalProfit) || 0),
    0,
  );

  const totalPremixProfit = broker1PremixProfit + broker2PremixProfit || wsOpenOrdersProfit;
  const closedTradesTotalCount = closedTradesCount ?? closedTrades.length;
  const overallPremixProfit =
    closedTradesTotalProfit ??
    closedTrades.reduce((sum, t) => sum + (parseFloat(t.totalProfit) || 0), 0);
  useEffect(() => {
    const now = Date.now();
    if (profitBatchAtRef.current && now - profitBatchAtRef.current < PROFIT_FRESH_MS) {
      return;
    }
    setRealTimeProfits({
      broker1: broker1PremixProfit,
      broker2: broker2PremixProfit,
      total: totalPremixProfit,
    });
  }, [broker1PremixProfit, broker2PremixProfit, totalPremixProfit]);
  if (DEBUG) {
    debugDashboard("Premix P&L Debug:", {
      activeTradesCount: activeTrades.length,
      sampleTrade: activeTrades[0] || null,
      sampleTradeFields: activeTrades[0] ? Object.keys(activeTrades[0]) : [],
      broker1PremixProfit,
      broker2PremixProfit,
      wsOpenOrdersProfit,
      totalPremixProfit,
      calculation: {
        fromBrokerProfits: broker1PremixProfit + broker2PremixProfit,
        fromTotalProfit: wsOpenOrdersProfit,
      },
      tradesProfitFields: activeTrades.map((t) => ({
        id: t.id || t.tradeId,
        broker1Profit: t.broker1Profit,
        broker2Profit: t.broker2Profit,
        currentProfit: t.currentProfit,
        profit: t.profit,
        totalProfit: t.totalProfit,
      })),
    });
  }
  const openOrdersStats = useMemo(() => {
    const orders = Array.isArray(filteredOpenOrdersSnapshot) ? filteredOpenOrdersSnapshot : [];
    const pos1 = orders.filter((o) => Number(o?.brokerPosition) === 1);
    const pos2 = orders.filter((o) => Number(o?.brokerPosition) === 2);

    const readLots = (o) =>
      Number(o?.lots ?? o?.Lots ?? o?.volume ?? o?.Volume ?? o?.volumeSize ?? o?.VolumeSize ?? 0);
    const readOpenPrice = (o) => Number(o?.openPrice ?? o?.OpenPrice ?? 0);

    const sumLots = (rows) =>
      rows.reduce((sum, o) => {
        const lots = readLots(o);
        return sum + (Number.isFinite(lots) ? lots : 0);
      }, 0);

    const wAvgPrice = (rows) => {
      const lotsTotal = sumLots(rows);
      if (lotsTotal <= 0) return 0;
      const weighted = rows.reduce((sum, o) => {
        const lots = readLots(o);
        const openPrice = readOpenPrice(o);
        if (!Number.isFinite(lots) || !Number.isFinite(openPrice)) return sum;
        return sum + openPrice * lots;
      }, 0);
      return weighted / lotsTotal;
    };

    const pos1Lots = sumLots(pos1);
    const pos2Lots = sumLots(pos2);
    const primaryLots = pos1Lots > 0 ? pos1Lots : pos2Lots;
    const openOrdersCount = Math.max(pos1.length, pos2.length);

    const avgFutureOpen = wAvgPrice(pos1);
    const avgSpotOpen = wAvgPrice(pos2);
    const avgPremium = pos1Lots > 0 && pos2Lots > 0 ? avgFutureOpen - avgSpotOpen : 0;

    return { openOrdersCount, primaryLots, avgPremium };
  }, [filteredOpenOrdersSnapshot]);

  const visibleFutureQuote = currentSet.symbolsLocked
    ? lockedFutureQuote || futureQuote
    : futureQuote;
  const visibleSpotQuote = currentSet.symbolsLocked ? lockedSpotQuote || spotQuote : spotQuote;
  const applyPremiumSnapshot = useCallback(
    (snapshot) => {
      if (!snapshot || typeof snapshot !== "object") return;

      const nextSourceLabel = deriveQuoteSourceLabel(snapshot);
      if (nextSourceLabel) {
        setDisplayQuoteSourceLabel((prev) => (prev === nextSourceLabel ? prev : nextSourceLabel));
      }

      const nextBuyPremium = Number(snapshot.buyPremium);
      const nextSellPremium = Number(snapshot.sellPremium);

      if (Number.isFinite(nextBuyPremium)) {
        setBuyPremium(nextBuyPremium);
      }
      if (Number.isFinite(nextSellPremium)) {
        setSellPremium(nextSellPremium);
      }

      if (snapshot.futureQuote) {
        setFutureQuote(snapshot.futureQuote);
        if (currentSet.symbolsLocked) {
          setLockedFutureQuote(snapshot.futureQuote);
        }
      }

      if (snapshot.spotQuote) {
        setSpotQuote(snapshot.spotQuote);
        if (currentSet.symbolsLocked) {
          setLockedSpotQuote(snapshot.spotQuote);
        }
      }

      if (isMarketPausePayload(snapshot)) {
        showMarketPauseWarning();
        setQuoteFeedStatus("");
      } else if (snapshot.stale || snapshot.collectorStale || snapshot.premiumLive === false) {
        marketPauseToastShownRef.current = false;
        setQuoteFeedStatus("Market Closed");
      } else {
        marketPauseToastShownRef.current = false;
        setQuoteFeedStatus("");
      }
    },
    [
      currentSet.symbolsLocked,
      deriveQuoteSourceLabel,
      isMarketPausePayload,
      showMarketPauseWarning,
    ],
  );

  const activeOpenOrdersCount = filteredActiveTrades.length;
  const activeOpenLots = filteredActiveTrades.reduce((sum, t) => {
    const lots = Number(
      t?.broker1Volume ?? t?.broker2Volume ?? t?.volume ?? t?.lots ?? t?.Lots ?? 0,
    );
    return sum + (Number.isFinite(lots) ? lots : 0);
  }, 0);
  const activeWeightedPremium = filteredActiveTrades.reduce((sum, t) => {
    const premium = Number(t?.executionPremium ?? 0);
    const lotSize = Number(
      t?.broker1Volume ?? t?.broker2Volume ?? t?.volume ?? t?.lots ?? t?.Lots ?? 0,
    );
    if (!Number.isFinite(premium) || !Number.isFinite(lotSize)) return sum;
    return sum + premium * lotSize;
  }, 0);
  const activeAvgPremium = activeOpenLots > 0 ? activeWeightedPremium / activeOpenLots : 0;

  const totalOpenOrders =
    activeOpenOrdersCount > 0 ? activeOpenOrdersCount : openOrdersStats.openOrdersCount;
  const totalOpenLots = activeOpenLots > 0 ? activeOpenLots : openOrdersStats.primaryLots;
  const avgPremium = activeOpenLots > 0 ? activeAvgPremium : openOrdersStats.avgPremium;
  const userDisplayName = user?.displayName || user?.email || "User";
  const accountSetOptions = useMemo(
    () =>
      accountSets.map((set) => ({
        value: set._id,
        label: set.name,
      })),
    [accountSets],
  );
  const futureOptions = useMemo(() => {
    if (currentSet?.symbolsLocked)
      return mapSymbolOptions([currentSet.futureSymbol].filter(Boolean), futureSymbol);
    if (currentSet?.requestedFutureSymbol)
      return [{ value: currentSet.requestedFutureSymbol, label: currentSet.requestedFutureSymbol }];
    return mapSymbolOptions(broker1Symbols, futureSymbol);
  }, [
    broker1Symbols,
    currentSet?.futureSymbol,
    currentSet?.requestedFutureSymbol,
    currentSet?.symbolsLocked,
    futureSymbol,
    mapSymbolOptions,
  ]);

  const spotOptions = useMemo(() => {
    if (currentSet?.symbolsLocked)
      return mapSymbolOptions([currentSet.spotSymbol].filter(Boolean), spotSymbol);
    if (currentSet?.requestedSpotSymbol)
      return [{ value: currentSet.requestedSpotSymbol, label: currentSet.requestedSpotSymbol }];
    return mapSymbolOptions(broker2Symbols, spotSymbol);
  }, [
    broker2Symbols,
    currentSet?.spotSymbol,
    currentSet?.requestedSpotSymbol,
    currentSet?.symbolsLocked,
    spotSymbol,
    mapSymbolOptions,
  ]);

  useEffect(() => {
    if (!selectedSetId) {
      lastWsConnectionState.current = null;
      return;
    }

    if (lastWsConnectionState.current === null) {
      lastWsConnectionState.current = wsConnected;
      return;
    }

    if (lastWsConnectionState.current === wsConnected) return;

    if (wsConnected) {
      toast.success("WebSocket connected");
    } else {
      toast.error("WebSocket disconnected");
    }

    lastWsConnectionState.current = wsConnected;
  }, [selectedSetId, toast, wsConnected]);

  const handleSmartSymbolRefresh = useCallback(
    async (typedSymbol, symbolsList, brokerId, terminal) => {
      if (typedSymbol.length > 2) {
        try {
          debugDashboard("Dashboard: Smart symbol search triggered for:", typedSymbol);
          const brokerToSearch = currentSet.brokers?.find((b) => (b.id || b._id) === brokerId);
          if (brokerToSearch) {
            const result = await symbolsCache.searchAndUpdateSymbol(
              typedSymbol,
              symbolsList,
              brokerToSearch,
            );

            if (result.symbols.length > 0) {
              if (brokerId === broker1Id) {
                setBroker1Symbols(result.symbols);
              } else if (brokerId === broker2Id) {
                setBroker2Symbols(result.symbols);
              }

              if (result.found) {
                debugDashboard(
                  `Dashboard: Symbol "${typedSymbol}" found after search for ${terminal}`,
                );
              } else {
                debugDashboard(
                  `Dashboard: Symbol "${typedSymbol}" not found, but cache updated for ${terminal}`,
                );
              }
            }
          }
        } catch (err) {
          console.error("❌ Smart symbol search failed:", err);
        }
      }
    },
    [broker1Id, broker2Id, currentSet.brokers, debugDashboard],
  );

  const handleFutureSelect = useCallback(
    async (value) => {
      setFutureSymbol(value);
      setFutureQuote(null);
      await handleSmartSymbolRefresh(value, broker1Symbols, broker1Id, broker1Term);
      if (selectedSetId && value && spotSymbol) {
        subscribeToQuotes(selectedSetId, value, spotSymbol);
      }
    },
    [broker1Id, broker1Symbols, broker1Term, handleSmartSymbolRefresh, selectedSetId, spotSymbol],
  );

  const handleSpotSelect = useCallback(
    async (value) => {
      setSpotSymbol(value);
      setSpotQuote(null);
      await handleSmartSymbolRefresh(value, broker2Symbols, broker2Id, broker2Term);
      if (selectedSetId && futureSymbol && value) {
        subscribeToQuotes(selectedSetId, futureSymbol, value);
      }
    },
    [broker2Id, broker2Symbols, broker2Term, futureSymbol, handleSmartSymbolRefresh, selectedSetId],
  );

  const loadActiveTradesData = useCallback(async () => {
    if (!selectedSetId) return;

    try {
      const symbolsOnlyMessage = (msg) =>
        /symbols\s*-?only|collector\s*-?only/i.test(String(msg || ""));
      let res;
      try {
        res = await API.get(`/trading/active-trades?accountSetId=${selectedSetId}`);
        if (res.data?.success === false && symbolsOnlyMessage(res.data?.message)) {
          res = await API.get(`/account-sets/${selectedSetId}/active-trades`);
        }
      } catch (err) {
        if (symbolsOnlyMessage(err?.response?.data?.message)) {
          res = await API.get(`/account-sets/${selectedSetId}/active-trades`);
        } else {
          throw err;
        }
      }
      const trades = res.data?.trades || res.data?.data?.trades || [];
      const ok = res.data?.success !== false;
      if (ok) {
        debugDashboard("Dashboard: Active trades loaded:", trades);
        const tradesWithProfits = (Array.isArray(trades) ? trades : []).map(
          normalizeDashboardTrade,
        );
        debugDashboard("Dashboard: Trades with profits calculated:", tradesWithProfits);
        setActiveTrades((prev) => {
          const prevById = new Map(prev.map((t) => [String(t.tradeId || t.id || ""), t]));
          return tradesWithProfits.map((trade) => {
            const tradeId = String(trade.tradeId || trade.id || "");
            const prevTrade = prevById.get(tradeId);
            const sameAccountSet =
              String(prevTrade?.accountSetId || "") ===
              String(trade.accountSetId || selectedSetId || "");
            return prevTrade && sameAccountSet
              ? mergeTradeSnapshotMetrics(trade, prevTrade)
              : trade;
          });
        });
      }
    } catch (err) {
      console.error("Failed to load active trades:", err);
      setActiveTrades([]);
    }
  }, [selectedSetId, debugDashboard]);

  const loadPendingOrdersData = useCallback(async () => {
    if (!selectedSetId) {
      setPendingOrders([]);
      return;
    }

    try {
      const res = await API.get(`/trading/pending-orders?accountSetId=${selectedSetId}`);
      if (res.data?.success) {
        setPendingOrders(Array.isArray(res.data.orders) ? res.data.orders : []);
      }
    } catch (err) {
      console.error("Failed to load pending orders:", err);
      setPendingOrders([]);
    }
  }, [selectedSetId]);

  const loadClosedTradesData = useCallback(async () => {
    if (!selectedSetId) return;

    try {
      const params = new URLSearchParams({ accountSetId: selectedSetId });
      const isSymbolsOnly = (msg, status) =>
        status === 410 || /symbols\s*-?only|collector\s*-?only/i.test(String(msg || ""));
      let res;
      try {
        res = await API.get(`/trading/closed-trades?${params.toString()}`);
        if (res.data?.success === false && isSymbolsOnly(res.data?.message, res.status)) {
          res = await API.get(`/account-sets/${selectedSetId}/closed-trades?${params.toString()}`);
        }
      } catch (err) {
        if (isSymbolsOnly(err?.response?.data?.message, err?.response?.status)) {
          res = await API.get(`/account-sets/${selectedSetId}/closed-trades?${params.toString()}`);
        } else {
          throw err;
        }
      }
      if (res.data.success) {
        const trades = res.data.trades || [];
        debugDashboard("Dashboard: Closed trades loaded:", trades);
        debugDashboard(
          "Dashboard: Closed trades sample profit fields:",
          trades[0]
            ? {
                totalProfit: trades[0].totalProfit,
                broker1Profit: trades[0].broker1Profit,
                broker2Profit: trades[0].broker2Profit,
              }
            : "No trades",
        );
        setClosedTrades(trades);
        const countValue = Number(res.data.count);
        setClosedTradesCount(Number.isFinite(countValue) ? countValue : trades.length);
        if (res.data.totalProfit !== undefined && res.data.totalProfit !== null) {
          setClosedTradesTotalProfit(parseFloat(res.data.totalProfit) || 0);
        } else {
          const total = trades.reduce(
            (sum, trade) => sum + (parseFloat(trade.totalProfit) || 0),
            0,
          );
          setClosedTradesTotalProfit(total);
        }
      }
    } catch (err) {
      console.error("Failed to load closed trades:", err);
      setClosedTrades([]);
    }
  }, [selectedSetId, debugDashboard]);

  useEffect(() => {
    if (selectedSetId) {
      loadActiveTradesData();
      loadPendingOrdersData();
      loadClosedTradesData();
      debugDashboard("Account set changed, clearing balance cache for fresh data");
      setBrokerBalances({});
      setOpenOrdersSnapshot([]);
    }
  }, [
    selectedSetId,
    loadActiveTradesData,
    loadPendingOrdersData,
    loadClosedTradesData,
    debugDashboard,
  ]);

  useEffect(() => {
    if (!selectedSetId) return;

    const refreshPendingOrdersData = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      loadPendingOrdersData();
    };

    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        refreshPendingOrdersData();
      }
    };

    const intervalId = setInterval(() => {
      refreshPendingOrdersData();
    }, PENDING_ORDERS_FALLBACK_REFRESH_MS);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      clearInterval(intervalId);
    };
  }, [loadPendingOrdersData, selectedSetId]);

  useEffect(() => {
    let cancelled = false;

    const bootstrapDashboard = async () => {
      const fallbackCode =
        process.env.REACT_APP_DEFAULT_REFERRAL_CODE ||
        Math.random().toString(36).substring(2, 10).toUpperCase();

      const [userRes, tradingRes, accountSetsRes] = await Promise.allSettled([
        getCurrentUser(),
        API.get("/trading/status"),
        getAccountSets({ force: true }),
      ]);

      if (cancelled) return;

      if (userRes.status === "fulfilled" && userRes.value) {
        setUser(userRes.value);
      } else {
        setUser({ email: "User", referralCode: fallbackCode });
      }

      if (tradingRes.status === "fulfilled") {
        setTradingStatus(
          tradingRes.value.data.available
            ? "Trading service available."
            : "Trading service unavailable.",
        );
      } else {
        setTradingStatus("Trading service unavailable.");
      }

      if (accountSetsRes.status === "fulfilled" && Array.isArray(accountSetsRes.value)) {
        const accountSetsData = accountSetsRes.value;
        setAccountSets(accountSetsData);
        if (accountSetsData.length) {
          const selectedId = resolveAccountSetId(accountSetsData);
          setSelectedSetId(selectedId);
          if (selectedId) setStoredAccountSetId(selectedId);
          const selectedSet =
            accountSetsData.find((s) => (s._id || s.id) === selectedId) || accountSetsData[0];
          if (selectedSet?.symbolsLocked && selectedSet.futureSymbol && selectedSet.spotSymbol) {
            setFutureSymbol(selectedSet.futureSymbol);
            setSpotSymbol(selectedSet.spotSymbol);
          }
        }
      } else {
        setAccountSets([]);
      }

      setDashboardLoading(false);
    };

    bootstrapDashboard();

    return () => {
      cancelled = true;
      if (wsStatusCheckInterval.current) {
        clearInterval(wsStatusCheckInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    const set = accountSets.find((s) => s._id === selectedSetId);
    if (set && set.symbolsLocked && set.futureSymbol && set.spotSymbol) {
      setFutureSymbol(set.futureSymbol);
      setSpotSymbol(set.spotSymbol);
    } else if (set && !set.symbolsLocked) {
      setFutureSymbol(set.requestedFutureSymbol || "");
      setSpotSymbol(set.requestedSpotSymbol || "");
    }
  }, [selectedSetId, accountSets, currentSet.symbolsLocked]);

  useEffect(() => {
    if (selectedSetId) {
      debugDashboard("Account set changed, clearing stale data and quote subscriptions");
      setFutureQuote(null);
      setSpotQuote(null);
      setBuyPremium(0);
      setSellPremium(0);
      setLockedFutureQuote(null);
      setLockedSpotQuote(null);
      setDisplayQuoteSourceLabel("");
      setBrokerBalances({});
      setRealTimeProfits({ broker1: 0, broker2: 0, total: 0 });
      setPendingOrders([]);
      collectorProfitAtRef.current = 0;
      profitBatchAtRef.current = 0;
      lastCollectorQuoteAt.current = 0;
      try {
        if (b1TicketProfitRef?.current) b1TicketProfitRef.current = new Map();
        if (b2TicketProfitRef?.current) b2TicketProfitRef.current = new Map();
        if (b1TicketSwapRef?.current) b1TicketSwapRef.current = new Map();
        if (b2TicketSwapRef?.current) b2TicketSwapRef.current = new Map();
      } catch {}
      setDisplayProfits({ broker1: 0, broker2: 0, total: 0 });
      clearQuoteSubscriptions();
    }
  }, [selectedSetId, debugDashboard]);

  useEffect(() => {
    if (!selectedSetId || currentSet.symbolsLocked) return;
    if (!futureSymbol || !spotSymbol) return;

    subscribeToQuotes(selectedSetId, futureSymbol, spotSymbol);
  }, [currentSet.symbolsLocked, futureSymbol, selectedSetId, spotSymbol]);
  useEffect(() => {
    if (!currentSet.symbolsLocked) return;

    let cancelled = false;

    const fetchLockedPremiumSnapshot = async () => {
      try {
        const response = await API.get("/premium-snapshot", {
          params: { accountSetId: selectedSetId },
        });

        if (cancelled) return;

        if (response.data?.success && response.data?.data) {
          applyPremiumSnapshot(response.data.data);
        }
      } catch (err) {
        console.error("❌ Locked quotes fetch error:", err);
        if (cancelled) return;
        setLockedFutureQuote(null);
        setLockedSpotQuote(null);
      }
    };

    fetchLockedPremiumSnapshot();
    return () => {
      cancelled = true;
    };
  }, [
    applyPremiumSnapshot,
    currentSet.symbolsLocked,
    currentSet.futureSymbol,
    currentSet.spotSymbol,
    selectedSetId,
  ]);
  const syncRealTimeProfitsFromRefs = useCallback(() => {
    const broker1TotalProfit = sumMapValues(b1TicketProfitRef.current);
    const broker2TotalProfit = sumMapValues(b2TicketProfitRef.current);
    const broker1TotalSwap = sumMapValues(b1TicketSwapRef.current);
    const broker2TotalSwap = sumMapValues(b2TicketSwapRef.current);

    setRealTimeProfits({
      broker1: broker1TotalProfit + broker1TotalSwap,
      broker2: broker2TotalProfit + broker2TotalSwap,
      total: broker1TotalProfit + broker2TotalProfit + broker1TotalSwap + broker2TotalSwap,
    });
  }, []);
  const handleRealTimeProfitUpdate = useCallback(
    (msg) => {
      const asid = msg?.accountSetId;
      if (asid && asid !== selectedSetId) return;

      const profitUpdates = msg.data || msg;
      const now = Date.now();
      const hasCollector =
        Array.isArray(profitUpdates) &&
        profitUpdates.some((u) => String(u?.source || "").toLowerCase() === "collector");
      if (hasCollector) {
        collectorProfitAtRef.current = now;
      } else if (
        collectorProfitAtRef.current &&
        now - collectorProfitAtRef.current < PROFIT_FRESH_MS
      ) {
        return;
      }
      if (!Array.isArray(profitUpdates) || !currentSet.brokers?.length || !selectedSetId) {
        return;
      }

      profitBatchAtRef.current = now;
      const lookup = buildBrokerScopedLookup(profitUpdates, (update) => ({
        ticket: update?.ticket,
        symbol: update?.symbol ?? update?.Symbol ?? "",
        brokerPosition: resolveLiveUpdatePosition(update, activeTradesRef.current),
        profit: Number(update?.profit) || 0,
        swap: toFiniteOrNull(update?.swap),
      }));

      setActiveTrades((prev) =>
        prev.map((trade) => {
          const next = { ...trade };
          let touched = false;

          const b1 = findBrokerScopedPayload(trade, 1, lookup);
          if (b1) {
            next.broker1Profit = b1.profit ?? 0;
            next.broker1Swap = mergeSwapValue(trade.broker1Swap, b1.swap);
            touched = true;
          }

          const b2 = findBrokerScopedPayload(trade, 2, lookup);
          if (b2) {
            next.broker2Profit = b2.profit ?? 0;
            next.broker2Swap = mergeSwapValue(trade.broker2Swap, b2.swap);
            touched = true;
          }

          return touched ? withComputedTradeFields(next) : trade;
        }),
      );

      for (const update of profitUpdates) {
        const ticketStr = String(update?.ticket ?? "").trim();
        if (!ticketStr) continue;

        const pos = resolveLiveUpdatePosition(update, activeTradesRef.current);
        const profit = Number(update?.profit) || 0;
        const swap = toFiniteOrNull(update?.swap);

        if (pos === 1) {
          b1TicketProfitRef.current.set(ticketStr, profit);
          const nextSwap = mergeSwapValue(b1TicketSwapRef.current.get(ticketStr), swap);
          if (nextSwap != null) b1TicketSwapRef.current.set(ticketStr, nextSwap);
        } else if (pos === 2) {
          b2TicketProfitRef.current.set(ticketStr, profit);
          const nextSwap = mergeSwapValue(b2TicketSwapRef.current.get(ticketStr), swap);
          if (nextSwap != null) b2TicketSwapRef.current.set(ticketStr, nextSwap);
        }
      }

      syncRealTimeProfitsFromRefs();
    },
    [currentSet.brokers, selectedSetId, syncRealTimeProfitsFromRefs],
  );
  const handlePositionsProfitUpdate = useCallback(
    (msg) => {
      const payload = msg?.data ?? msg;
      const asid = payload?.accountSetId ?? msg?.accountSetId;
      if (asid && asid !== selectedSetId) return;

      const now = Date.now();
      const source = String(payload?.source || "").toLowerCase();
      if (source.includes("collector")) {
        collectorProfitAtRef.current = now;
      } else if (
        collectorProfitAtRef.current &&
        now - collectorProfitAtRef.current < PROFIT_FRESH_MS
      ) {
        return;
      }

      const mt5Data = Array.isArray(payload?.mt5Data) ? payload.mt5Data : [];
      const mt4Data = Array.isArray(payload?.mt4Data) ? payload.mt4Data : [];
      const positions = [...mt5Data, ...mt4Data];
      if (!positions.length || !currentSet.brokers?.length || !selectedSetId) {
        return;
      }

      profitBatchAtRef.current = now;

      const lookup = buildBrokerScopedLookup(positions, (position) => ({
        ticket: position?.ticket,
        symbol: position?.symbol ?? position?.Symbol ?? "",
        brokerPosition: resolveLiveUpdatePosition(position, activeTradesRef.current),
        profit: Number(position?.profit) || 0,
        swap: toFiniteOrNull(position?.swap),
      }));

      setActiveTrades((prev) =>
        prev.map((trade) => {
          const next = { ...trade };
          let touched = false;

          const p1 = findBrokerScopedPayload(trade, 1, lookup);
          if (p1) {
            next.broker1Profit = p1.profit ?? 0;
            next.broker1Swap = mergeSwapValue(trade.broker1Swap, p1.swap);
            touched = true;
          }

          const p2 = findBrokerScopedPayload(trade, 2, lookup);
          if (p2) {
            next.broker2Profit = p2.profit ?? 0;
            next.broker2Swap = mergeSwapValue(trade.broker2Swap, p2.swap);
            touched = true;
          }

          return touched ? withComputedTradeFields(next) : trade;
        }),
      );

      for (const position of positions) {
        const ticketStr = String(position?.ticket ?? "").trim();
        if (!ticketStr) continue;

        const brokerPosition = resolveLiveUpdatePosition(position, activeTradesRef.current);
        const profit = Number(position?.profit) || 0;
        const swap = toFiniteOrNull(position?.swap);

        if (brokerPosition === 1) {
          b1TicketProfitRef.current.set(ticketStr, profit);
          const nextSwap = mergeSwapValue(b1TicketSwapRef.current.get(ticketStr), swap);
          if (nextSwap != null) b1TicketSwapRef.current.set(ticketStr, nextSwap);
        } else if (brokerPosition === 2) {
          b2TicketProfitRef.current.set(ticketStr, profit);
          const nextSwap = mergeSwapValue(b2TicketSwapRef.current.get(ticketStr), swap);
          if (nextSwap != null) b2TicketSwapRef.current.set(ticketStr, nextSwap);
        }
      }

      syncRealTimeProfitsFromRefs();
    },
    [currentSet.brokers, selectedSetId, syncRealTimeProfitsFromRefs],
  );
  const handleBalanceBatch = useCallback(
    (balanceUpdates) => {
      debugDashboard("Processing batched balance updates:", Object.keys(balanceUpdates).length);

      const processedUpdates = {};

      Object.entries(balanceUpdates).forEach(([key, data]) => {
        const brokerId = data.brokerId || data.data?.brokerId;
        const balanceData = data.data || data;

        if (!brokerId || !currentSet.brokers?.length || !selectedSetId) {
          return;
        }
        const broker = currentSet.brokers?.find((x) => x.id === brokerId || x._id === brokerId);

        if (!broker) {
          debugDashboard("Skipping balance update: broker not found in current account set", {
            brokerId,
            accountSetId: selectedSetId,
          });
          return;
        }
        const brokerKey = createBrokerKey(broker);
        if (brokerKey) {
          const balance = parseFloat(balanceData.balance) || 0;
          const equity = parseFloat(balanceData.equity) || 0;
          const margin = parseFloat(balanceData.margin) || 0;
          const freeMargin = parseFloat(balanceData.freeMargin) || 0;
          const marginLevelRaw = parseFloat(balanceData.marginLevel);
          const marginLevel = Number.isFinite(marginLevelRaw)
            ? marginLevelRaw
            : margin > 0
              ? (equity / margin) * 100
              : 0;
          processedUpdates[brokerKey] = {
            balance,
            equity,
            profit: 0,
            margin,
            freeMargin,
            marginLevel,
            leverage: parseFloat(balanceData.leverage) || 1,
            lastUpdate: Date.now(),
          };
        }
      });

      if (Object.keys(processedUpdates).length > 0) {
        setBrokerBalances((prev) => ({ ...prev, ...processedUpdates }));
        setWsConnected(true);
        debugDashboard("Updated broker balances:", Object.keys(processedUpdates).length);
      }
    },
    [currentSet.brokers, selectedSetId, debugDashboard],
  );

  const handleQuoteBatch = useCallback(
    (quoteUpdates) => {
      debugDashboard("Processing batched quote updates:", Object.keys(quoteUpdates).length);

      Object.values(quoteUpdates).forEach((data) => {
        const futureValid = isQuoteFresh(data.futureQuote);
        const spotValid = isQuoteFresh(data.spotQuote);

        if (!futureValid || !spotValid) {
          debugDashboard("Received stale quotes in batch:", {
            futureAge: data.futureQuote
              ? Date.now() - new Date(data.futureQuote.timestamp)
              : "no quote",
            spotAge: data.spotQuote ? Date.now() - new Date(data.spotQuote.timestamp) : "no quote",
          });
        }
        const futureValidLocal =
          data.futureQuoteFresh !== undefined
            ? data.futureQuoteFresh
            : isQuoteFresh(data.futureQuote);
        const spotValidLocal =
          data.spotQuoteFresh !== undefined ? data.spotQuoteFresh : isQuoteFresh(data.spotQuote);

        debugDashboard("Symbols locked:", currentSet.symbolsLocked);
        debugDashboard("Expected symbols:", {
          future: currentSet.symbolsLocked ? currentSet.futureSymbol : futureSymbol,
          spot: currentSet.symbolsLocked ? currentSet.spotSymbol : spotSymbol,
        });
        debugDashboard("Quote freshness:", { futureValidLocal, spotValidLocal });
        if (currentSet.symbolsLocked) {
          debugDashboard("Processing locked symbols update");
          if (data.futureSymbol === currentSet.futureSymbol && data.futureQuote) {
            debugDashboard("Updating future quote for locked symbols:", data.futureQuote);
            setFutureQuote(data.futureQuote);
            setLockedFutureQuote(data.futureQuote);
          }

          if (data.spotSymbol === currentSet.spotSymbol) {
            if (data.spotQuote) {
              debugDashboard("Updating spot quote for locked symbols:", data.spotQuote);
              setSpotQuote(data.spotQuote);
              setLockedSpotQuote(data.spotQuote);
            }
          }
        } else {
          debugDashboard("Processing unlocked symbols update");
          if (data.futureSymbol === futureSymbol && data.futureQuote) {
            debugDashboard("Updating future quote for unlocked symbols:", data.futureQuote);
            setFutureQuote(data.futureQuote);
          }
          if (data.spotSymbol === spotSymbol) {
            if (data.spotQuote) {
              debugDashboard("Updating spot quote for unlocked symbols:", data.spotQuote);
              setSpotQuote(data.spotQuote);
            }
          }
        }
      });
    },
    [
      currentSet.symbolsLocked,
      currentSet.futureSymbol,
      currentSet.spotSymbol,
      futureSymbol,
      spotSymbol,
      debugDashboard,
    ],
  );
  useEffect(() => {
    if (!selectedSetId) {
      debugDashboard("Skipping WebSocket setup - missing selectedSetId");
      return;
    }

    if (!currentSet.brokers || currentSet.brokers.length === 0) {
      debugDashboard("Skipping WebSocket setup - no brokers available yet");
      return;
    }

    debugDashboard("Setting up WebSocket connection:", {
      selectedSetId,
      symbolsLocked: currentSet.symbolsLocked,
      futureSymbol: currentSet.symbolsLocked ? currentSet.futureSymbol : futureSymbol,
      spotSymbol: currentSet.symbolsLocked ? currentSet.spotSymbol : spotSymbol,
    });

    const handleConnectionConfirmed = (data) => {
      debugDashboard("WebSocket connection confirmed:", data);
      setWsConnected(true);
      loadPendingOrdersData();
    };

    const handleSubscriptionConfirmed = (data) => {
      debugDashboard("Subscription confirmed:", data);
      setWsConnected(true);
      const payload = data?.data ?? data;
      const incomingAccountSetId = data?.accountSetId ?? payload?.accountSetId;
      if (!incomingAccountSetId || String(incomingAccountSetId) === String(selectedSetId)) {
        loadPendingOrdersData();
      }
    };
    const fetchLatestQuote = async (symbol, type) => {
      try {
        let brokerId, terminal;
        if (type === "spot") {
          const broker = brokers.find((b) => b.position === 2) || brokers[1];
          brokerId = broker?.id;
          terminal = broker?.terminal;
        } else {
          const broker = brokers.find((b) => b.position === 1) || brokers[0];
          brokerId = broker?.id;
          terminal = broker?.terminal;
        }

        if (brokerId && terminal) {
          const response = await API.get(`/trading/quote/${symbol}`, {
            params: { terminal, id: brokerId },
          });

          if (response.data.success && response.data.data) {
            const quote = {
              bid: parseFloat(response.data.data.bid),
              ask: parseFloat(response.data.data.ask),
              symbol: symbol,
              timestamp: response.data.data.timestamp || new Date().toISOString(),
            };

            if (type === "spot") {
              debugDashboard("Fetched latest spot quote from cache:", quote);
              setSpotQuote(quote);
              if (currentSet.symbolsLocked) setLockedSpotQuote(quote);
            } else {
              debugDashboard("Fetched latest future quote from cache:", quote);
              setFutureQuote(quote);
              if (currentSet.symbolsLocked) setLockedFutureQuote(quote);
            }
          }
        }
      } catch (error) {
        console.error("❌ Failed to fetch latest quote for", symbol, ":", error.message);
      }
    };

    const handleQuoteUpdate = (data) => {
      const incomingSetId = data?.accountSetId;
      if (incomingSetId && String(incomingSetId) !== String(selectedSetId)) return;

      const incomingSource = normalizeQuoteSource(
        data?.futureQuote?.source || data?.spotQuote?.source || data?.source,
      );
      if (
        incomingSource === "db" &&
        lastCollectorQuoteAt.current > 0 &&
        Date.now() - lastCollectorQuoteAt.current < COLLECTOR_PREFER_MS
      ) {
        return;
      }
      if (incomingSource === "collector") {
        lastCollectorQuoteAt.current = Date.now();
      }
      const nextSourceLabel = deriveQuoteSourceLabel(data);
      if (nextSourceLabel) {
        setDisplayQuoteSourceLabel((prev) => (prev === nextSourceLabel ? prev : nextSourceLabel));
      }

      if (isMarketPausePayload(data)) {
        showMarketPauseWarning();
        setQuoteFeedStatus("");
      } else if (data?.collectorStale || data?.quoteLive === false) {
        marketPauseToastShownRef.current = false;
        setQuoteFeedStatus("Market Closed");
      } else {
        marketPauseToastShownRef.current = false;
        setQuoteFeedStatus("");
      }

      debugDashboard("Optimized quote update received:", data?.futureQuote?.source || data?.source);

      if (data.futureQuote?.source || data.spotQuote?.source) {
        const futureAge = data.futureQuote?.age || 0;
        const spotAge = data.spotQuote?.age || 0;
        debugDashboard(
          `WebSocket quotes: Future ${data.futureQuote?.source || "unknown"} (${futureAge}ms), Spot ${data.spotQuote?.source || "unknown"} (${spotAge}ms)`,
        );
      }

      const futureValid =
        data.futureQuoteFresh !== undefined
          ? data.futureQuoteFresh
          : isQuoteFresh(data.futureQuote);
      const spotValid =
        data.spotQuoteFresh !== undefined ? data.spotQuoteFresh : isQuoteFresh(data.spotQuote);

      debugDashboard("Symbols locked:", currentSet.symbolsLocked);
      debugDashboard("Expected symbols:", {
        future: currentSet.symbolsLocked ? currentSet.futureSymbol : futureSymbol,
        spot: currentSet.symbolsLocked ? currentSet.spotSymbol : spotSymbol,
      });
      debugDashboard("Quote freshness:", { futureValid, spotValid });

      if (currentSet.symbolsLocked) {
        debugDashboard("Processing locked symbols update");
        if (data.futureSymbol === currentSet.futureSymbol && data.futureQuote) {
          debugDashboard("Updating future quote for locked symbols:", data.futureQuote);
          setFutureQuote(data.futureQuote);
          setLockedFutureQuote(data.futureQuote);
        } else {
          debugDashboard("Future symbol mismatch or missing quote:", {
            expected: currentSet.futureSymbol,
            received: data.futureSymbol,
            hasQuote: !!data.futureQuote,
          });
        }

        if (data.spotSymbol === currentSet.spotSymbol) {
          if (data.spotQuote) {
            debugDashboard("Updating spot quote for locked symbols:", data.spotQuote);
            setSpotQuote(data.spotQuote);
            setLockedSpotQuote(data.spotQuote);
          } else {
            debugDashboard(
              "Spot quote is null, trying to fetch from cache for symbol:",
              data.spotSymbol,
            );
            fetchLatestQuote(data.spotSymbol, "spot");
          }
        } else {
          debugDashboard("Spot symbol mismatch:", {
            expected: currentSet.spotSymbol,
            received: data.spotSymbol,
            hasQuote: !!data.spotQuote,
          });
        }
      } else {
        debugDashboard("Processing unlocked symbols update");
        if (data.futureSymbol === futureSymbol && data.futureQuote) {
          debugDashboard("Updating future quote for unlocked symbols:", data.futureQuote);
          setFutureQuote(data.futureQuote);
        }
        if (data.spotSymbol === spotSymbol) {
          if (data.spotQuote) {
            debugDashboard("Updating spot quote for unlocked symbols:", data.spotQuote);
            setSpotQuote(data.spotQuote);
          } else {
            debugDashboard(
              "Spot quote is null, trying to fetch from cache for symbol:",
              data.spotSymbol,
            );
            fetchLatestQuote(data.spotSymbol, "spot");
          }
        }
      }
    };
    const handlePremiumUpdate = (data) => {
      const incomingSetId = data?.accountSetId;
      if (incomingSetId && String(incomingSetId) !== String(selectedSetId)) return;
      if (!currentSet.symbolsLocked) return;

      if (
        data.futureSymbol &&
        currentSet.futureSymbol &&
        data.futureSymbol !== currentSet.futureSymbol
      )
        return;
      if (data.spotSymbol && currentSet.spotSymbol && data.spotSymbol !== currentSet.spotSymbol)
        return;

      applyPremiumSnapshot(data);
    };
    const handleBalanceUpdate = (data) => {
      const incomingSetId = data?.accountSetId;
      if (incomingSetId && String(incomingSetId) !== String(selectedSetId)) return;
      const brokerId = data.brokerId || data.data?.brokerId;
      const balanceData = data.data || data;

      if (!brokerId || !currentSet.brokers?.length || !selectedSetId) {
        return;
      }

      const broker = currentSet.brokers?.find((x) => x.id === brokerId || x._id === brokerId);

      if (!broker) {
        return;
      }
      const brokerKey = createBrokerKey(broker);
      if (brokerKey) {
        const balance = parseFloat(balanceData.balance) || 0;
        const equity = parseFloat(balanceData.equity) || 0;
        const margin = parseFloat(balanceData.margin) || 0;
        const freeMargin = parseFloat(balanceData.freeMargin) || 0;
        const marginLevelRaw = parseFloat(balanceData.marginLevel);
        const marginLevel = Number.isFinite(marginLevelRaw)
          ? marginLevelRaw
          : margin > 0
            ? (equity / margin) * 100
            : 0;
        setBrokerBalances((prev) => ({
          ...prev,
          [brokerKey]: {
            balance,
            equity,
            profit: parseFloat(balanceData.profit) || 0,
            margin,
            freeMargin,
            marginLevel,
            leverage: parseFloat(balanceData.leverage) || 1,
            lastUpdate: Date.now(),
          },
        }));
        setWsConnected(true);
        debugDashboard("Updated single balance for broker:", brokerKey);
      }
    };

    const handleError = (data) => {
      debugDashboard("WebSocket error received:", data);
      if (isMarketPausePayload(data)) {
        showMarketPauseWarning();
        return;
      }
      if (isCollectorAvailabilityNotice(data?.message)) {
        setQuoteFeedStatus(getCollectorAvailabilityLabel(data?.message));
        return;
      }
      showDashboardToast(
        data?.message || "Connection issue",
        isCollectorWarningPayload(data) ? "warning" : "error",
      );
    };

    const handleTradeMapping = (data) => {
      debugDashboard("Trade mapping received:", data);
      if (Array.isArray(data)) setTradeMapping(data);
    };

    const handleActiveTradesUpdate = (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = payload?.accountSetId;
      const orders = Array.isArray(payload?.orders) ? payload.orders : [];

      debugDashboard("WebSocket open orders update received:", {
        accountSetId,
        selectedSetId,
        ordersCount: orders.length,
        orders: orders.map((o) => ({
          ticket: o.ticket,
          brokerId: o.brokerId,
          brokerPosition: o.brokerPosition,
          profit: o.profit,
          symbol: o.symbol,
        })),
      });

      if (accountSetId !== selectedSetId) {
        debugDashboard("Ignoring orders update for different account set");
        return;
      }

      setOpenOrdersSnapshot(orders);

      setActiveTrades((prev) => {
        const byKey = new Map();
        const plainTicketCounts = new Map();

        for (const order of orders) {
          const ticketKey = String(order?.ticket ?? "").trim();
          if (!ticketKey) continue;
          plainTicketCounts.set(ticketKey, (plainTicketCounts.get(ticketKey) || 0) + 1);
        }

        for (const o of orders) {
          const keys = [];
          if (o.brokerId) keys.push(`${o.brokerId}:${o.ticket}`);
          if (o.brokerPosition != null) keys.push(`${o.brokerPosition}:${o.ticket}`);
          if (plainTicketCounts.get(String(o.ticket)) === 1) {
            keys.push(String(o.ticket));
          }
          keys.forEach((key) => byKey.set(key, o));
        }

        return prev.map((t) => {
          const o1Keys = [`${t.broker1Id}:${t.broker1Ticket}`, `1:${t.broker1Ticket}`];
          if (plainTicketCounts.get(String(t.broker1Ticket)) === 1) {
            o1Keys.push(String(t.broker1Ticket));
          }
          const o2Keys = [`${t.broker2Id}:${t.broker2Ticket}`, `2:${t.broker2Ticket}`];
          if (plainTicketCounts.get(String(t.broker2Ticket)) === 1) {
            o2Keys.push(String(t.broker2Ticket));
          }

          const o1 = o1Keys.map((key) => byKey.get(key)).find((order) => order);
          const o2 = o2Keys.map((key) => byKey.get(key)).find((order) => order);
          const next = { ...t };
          if ((next.broker1Profit === undefined || next.broker1Profit === null) && o1) {
            next.broker1Profit = Number(o1.profit) || 0;
          }
          if (o1) {
            next.broker1Swap = mergeSwapValue(next.broker1Swap, o1.swap);
          }
          if ((next.broker2Profit === undefined || next.broker2Profit === null) && o2) {
            next.broker2Profit = Number(o2.profit) || 0;
          }
          if (o2) {
            next.broker2Swap = mergeSwapValue(next.broker2Swap, o2.swap);
          }
          return withComputedTradeFields(next);
        });
      });
    };
    const unsubConnection = onMessage("connection", handleConnectionConfirmed);
    const unsubSubscription = onMessage("subscription_confirmed", handleSubscriptionConfirmed);
    const unsubPremium = onMessage("premium_update", handlePremiumUpdate);
    const unsubQuote = onMessage("quote_update", handleQuoteUpdate);
    const unsubBal = onMessage("balance", handleBalanceUpdate);
    const unsubErr = onMessage("error", handleError);
    const unsubTrade = onMessage("trade_mapping", handleTradeMapping);
    const unsubActiveOrders = onMessage("open_orders_update", handleActiveTradesUpdate);
    const unsubPendingOrders = onMessage("pending_order_update", (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = msg?.accountSetId ?? payload?.accountSetId;
      if (accountSetId && String(accountSetId) !== String(selectedSetId)) return;

      const nextOrderId = String(payload?.orderId || payload?.id || "");
      if (!nextOrderId) return;

      setPendingOrders((previous) => {
        const index = previous.findIndex(
          (order) => String(order.orderId || order.id || "") === nextOrderId,
        );
        const isVisible = ["Pending", "Executing"].includes(String(payload?.status || ""));

        if (!isVisible) {
          if (index < 0) return previous;
          const next = [...previous];
          next.splice(index, 1);
          return next;
        }

        if (index < 0) {
          return [...previous, payload];
        }

        const next = [...previous];
        next[index] = { ...next[index], ...payload };
        return next;
      });
    });
    const unsubOrderUpdate = onMessage("order_update", (msg) => {
      const payload = msg?.data ?? msg;
      const accountSetId = msg?.accountSetId ?? payload?.accountSetId;
      if (accountSetId && accountSetId !== selectedSetId) return;

      const ticket = String(payload?.ticket || "");
      const brokerPosition = Number(payload?.brokerPosition);
      const isClosed = Boolean(payload?.closeTime && payload?.closeTime !== "0001-01-01T00:00:00");

      if (!ticket || (brokerPosition !== 1 && brokerPosition !== 2)) return;

      if (isClosed) {
        loadActiveTradesData();
        if (brokerPosition === 1) b1TicketProfitRef.current.delete(ticket);
        if (brokerPosition === 2) b2TicketProfitRef.current.delete(ticket);
        if (brokerPosition === 1) b1TicketSwapRef.current.delete(ticket);
        if (brokerPosition === 2) b2TicketSwapRef.current.delete(ticket);
        return;
      }
      let matched = false;
      setActiveTrades((prev) =>
        prev.map((t) => {
          const matchB1 = brokerPosition === 1 && String(t.broker1Ticket) === ticket;
          const matchB2 = brokerPosition === 2 && String(t.broker2Ticket) === ticket;
          if (!matchB1 && !matchB2) return t;
          matched = true;
          const next = { ...t };
          if (payload?.tradeTimes && typeof payload.tradeTimes === "object") {
            next.tradeTimes = payload.tradeTimes;
          }
          if (matchB1) {
            if (payload?.openPrice != null) next.broker1OpenPrice = Number(payload.openPrice);
            if (payload?.openTime) next.broker1OpenTime = payload.openTime;
          } else if (matchB2) {
            if (payload?.openPrice != null) next.broker2OpenPrice = Number(payload.openPrice);
            if (payload?.openTime) next.broker2OpenTime = payload.openTime;
          }
          return next;
        }),
      );
      if (!matched) {
        subscribeToOpenOrders(selectedSetId);
        subscribeToPositions(selectedSetId);
      }
    });
    const unsubApiError = onMessage("api_error", (data) => {
      debugDashboard("API Error received:", data);
      if (isMarketPausePayload(data)) {
        showMarketPauseWarning();
        return;
      }
      showDashboardToast(
        data?.message || data?.data?.message || "API Error",
        isCollectorWarningPayload(data) ? "warning" : "error",
      );
    });
    const unsubBalanceBatch = onBalanceBatch(handleBalanceBatch);
    const unsubQuoteBatch = onQuoteBatch(handleQuoteBatch);
    const unsubRealTimeProfit = onMessage("order_profit_batch", handleRealTimeProfitUpdate);
    const unsubPositionsProfit = onMessage("positions_update", handlePositionsProfitUpdate);

    connectWS(selectedSetId);

    subscribeToOpenOrders(selectedSetId);
    subscribeToPositions(selectedSetId);

    const unsubProfitSvc = subscribeToOrderProfit(selectedSetId);

    if (currentSet.symbolsLocked && currentSet.futureSymbol && currentSet.spotSymbol) {
      debugDashboard("Subscribing to quotes for LOCKED symbols:", {
        futureSymbol: currentSet.futureSymbol,
        spotSymbol: currentSet.spotSymbol,
      });
      subscribeToPremium(selectedSetId, currentSet.futureSymbol, currentSet.spotSymbol);
      subscribeToQuotes(
        selectedSetId,
        currentSet.futureSymbol,
        currentSet.spotSymbol,
        handleQuoteUpdate,
      );
    } else {
      debugDashboard("Skipping quote subscription - symbols not locked or missing:", {
        symbolsLocked: currentSet.symbolsLocked,
        futureSymbol: currentSet.futureSymbol,
        spotSymbol: currentSet.spotSymbol,
      });
    }

    const statusCheck = setInterval(() => {
      setWsConnected(isWSConnected());
    }, 10000);

    wsStatusCheckInterval.current = statusCheck;

    return () => {
      debugDashboard("Cleaning up WebSocket subscriptions");
      clearInterval(statusCheck);
      unsubConnection();
      unsubSubscription();
      unsubPremium();
      unsubQuote();
      unsubApiError();
      unsubBal();
      unsubErr();
      unsubTrade();
      unsubActiveOrders();
      unsubPendingOrders();
      unsubOrderUpdate();
      unsubBalanceBatch();
      unsubQuoteBatch();
      unsubRealTimeProfit();
      unsubPositionsProfit();
      unsubProfitSvc && unsubProfitSvc();
    };
  }, [
    selectedSetId,
    futureSymbol,
    spotSymbol,
    brokers,
    currentSet.brokers,
    currentSet.symbolsLocked,
    currentSet.futureSymbol,
    currentSet.spotSymbol,
    applyPremiumSnapshot,
    handleBalanceBatch,
    handleQuoteBatch,
    handleRealTimeProfitUpdate,
    handlePositionsProfitUpdate,
    loadActiveTradesData,
    loadPendingOrdersData,
    debugDashboard,
    deriveQuoteSourceLabel,
    isMarketPausePayload,
    normalizeQuoteSource,
    isCollectorWarningPayload,
    showMarketPauseWarning,
    showDashboardToast,
  ]);

  const handleSubmitSymbols = async () => {
    if (currentLinkStatus !== "active") {
      const gateMeta = getDashboardApprovalGateMeta(currentLinkStatus);
      toast.error(gateMeta.actionMessage);
      return;
    }

    if (!futureSymbol || !spotSymbol) {
      toast.error("Please select both Future and Spot symbols");
      return;
    }

    setSubmitting(true);
    try {
      const response = await API.patch(`/account-sets/${selectedSetId}/symbols`, {
        futureSymbol,
        spotSymbol,
      });

      if (response.data.success) {
        toast.success("Symbols locked and premium tracking started");
        setAccountSets((prev) => {
          const next = prev.map((set) =>
            set._id === selectedSetId
              ? {
                  ...set,
                  symbolsLocked: true,
                  futureSymbol,
                  spotSymbol,
                  premiumTableName: response.data.tables?.premium,
                }
              : set,
          );
          setAccountSetsCache(next);
          return next;
        });
      } else {
        toast.error(response.data.error || "Failed to lock symbols");
      }
    } catch (err) {
      toast.error("Failed to save symbols: " + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlockSymbols = async () => {
    try {
      const response = await API.patch(`/account-sets/${selectedSetId}/unlock-symbols`);

      if (response.data.success) {
        toast.success("Symbols unlocked for new selection");
        setAccountSets((prev) => {
          const next = prev.map((set) =>
            set._id === selectedSetId
              ? {
                  ...set,
                  symbolsLocked: false,
                  futureSymbol: "",
                  spotSymbol: "",
                  premiumTableName: null,
                }
              : set,
          );
          setAccountSetsCache(next);
          return next;
        });
        setFutureSymbol("");
        setSpotSymbol("");
        setFutureQuote(null);
        setSpotQuote(null);
        setLockedFutureQuote(null);
        setLockedSpotQuote(null);
      }
    } catch (err) {
      toast.error(`Failed to unlock: ${err.response?.data?.error || err.message}`);
    }
  };
  useEffect(() => {
    debugDashboard("Dashboard: useEffect symbols loading triggered:", {
      selectedSetId,
      symbolsLocked: currentSet.symbolsLocked,
    });

    if (!selectedSetId) {
      setSymbolsLoading(false);
      return;
    }

    setBroker1Symbols([]);
    setBroker2Symbols([]);

    const loadSymbolsOptimized = async () => {
      setSymbolsLoading(true);

      debugDashboard("Dashboard: Starting optimized symbol loading for:", {
        selectedSetId,
        accountSetName: currentSet.name,
      });

      try {
        const result = await symbolsCache.getSymbolsForAccountSet(currentSet);

        if (result.success) {
          setBroker1Symbols(result.broker1Symbols);
          setBroker2Symbols(result.broker2Symbols);
          debugDashboard(
            `Dashboard: Loaded symbols via cache - Broker1: ${result.broker1Symbols.length}, Broker2: ${result.broker2Symbols.length}`,
          );

          if (!result.broker1Symbols.length && !result.broker2Symbols.length) {
            showDashboardToast(
              "No symbols found in cache. Symbols may still be loading in the background.",
              "warning",
            );
          }
        } else {
          console.error("❌ Dashboard: Failed to load symbols:", result.error);
          showDashboardToast(result.error || "Failed to load symbols");
        }
      } catch (err) {
        console.error("❌ Dashboard: Symbol loading error:", err);
        showDashboardToast(`Failed to load symbols: ${err.message}`);
      } finally {
        setSymbolsLoading(false);
        debugDashboard("Dashboard: Optimized symbol loading complete");
      }
    };

    loadSymbolsOptimized();
  }, [
    selectedSetId,
    currentSet,
    currentSet.symbolsLocked,
    currentSet.name,
    debugDashboard,
    showDashboardToast,
  ]);
  useEffect(() => {
    if (currentSet.symbolsLocked) return;

    const activeQuotes = currentSet.symbolsLocked
      ? { future: lockedFutureQuote || futureQuote, spot: lockedSpotQuote || spotQuote }
      : { future: futureQuote, spot: spotQuote };

    if (activeQuotes.future && activeQuotes.spot) {
      const newBuyPremium = (activeQuotes.future.ask || 0) - (activeQuotes.spot.bid || 0);
      const newSellPremium = (activeQuotes.future.bid || 0) - (activeQuotes.spot.ask || 0);

      setBuyPremium(newBuyPremium);
      setSellPremium(newSellPremium);
    } else {
      setBuyPremium(0);
      setSellPremium(0);
    }
  }, [futureQuote, spotQuote, lockedFutureQuote, lockedSpotQuote, currentSet.symbolsLocked]);

  if (dashboardLoading) {
    return (
      <DashboardContext.Provider
        value={{
          accountSets,
          selectedSetId,
          setSelectedSetId,
          selectedTimeframe,
          setSelectedTimeframe,
          tradeMapping,
        }}
      >
        <div className="modern-dashboard modern-dashboard-loading">
          <AppPageLoader
            label="Loading Dashboard"
            detail="Preparing account context, balances, and live market data..."
          />
        </div>
      </DashboardContext.Provider>
    );
  }

  return (
    <DashboardContext.Provider
      value={{
        accountSets,
        selectedSetId,
        setSelectedSetId,
        selectedTimeframe,
        setSelectedTimeframe,
        tradeMapping,
      }}
    >
      <div className="modern-dashboard">
        <main className="dashboard-content">
          <div className="dashboard-header">
            <h1>
              Welcome, <span className="dashboard-user-highlight">{userDisplayName}</span>
            </h1>
            <p>Monitor your trading performance and manage your accounts</p>
          </div>

          <div className="performance-grid">
            <div className="performance-card broker1">
              <div className="card-top">
                <div className="card-heading-group">
                  <span className="card-title-label">Broker 1 ({broker1Term || "Not Set"})</span>
                  <div className="card-subtitle-row">
                    <span className="card-subtitle-label">Margin Level %</span>
                    <span
                      className={`card-subtitle-value ${getKpiDetailValueClass(Number(b1.marginLevel || 0)).replace("card-detail-value ", "")}`}
                    >
                      {Number(b1.marginLevel || 0).toFixed(1)}
                    </span>
                  </div>
                </div>
                <div className="card-icon card-icon-broker1">
                  <TrendingUp size={24} />
                </div>
              </div>
              <div className="card-summary-copy">
                <strong className={getKpiValueClass(displayProfits.broker1)}>
                  {displayProfits.broker1 !== 0
                    ? `$${displayProfits.broker1.toLocaleString()}`
                    : "$0"}
                </strong>
                <div className="card-detail-list">
                  <div className="card-detail-item">
                    <span className="card-detail-label">Balance</span>
                    <span className={getKpiDetailValueClass(Number(b1.balance || 0))}>
                      ${(b1.balance || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Equity</span>
                    <span className={getKpiDetailValueClass(Number(b1.equity || 0))}>
                      ${(b1.equity || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Margin</span>
                    <span className={getKpiDetailValueClass(Number(b1.margin || 0))}>
                      ${(b1.margin || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Free Margin</span>
                    <span className={getKpiDetailValueClass(Number(b1.freeMargin || 0))}>
                      ${(b1.freeMargin || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="performance-card broker2">
              <div className="card-top">
                <div className="card-heading-group">
                  <span className="card-title-label">Broker 2 ({broker2Term || "Not Set"})</span>
                  <div className="card-subtitle-row">
                    <span className="card-subtitle-label">Margin Level %</span>
                    <span
                      className={`card-subtitle-value ${getKpiDetailValueClass(Number(b2.marginLevel || 0)).replace("card-detail-value ", "")}`}
                    >
                      {Number(b2.marginLevel || 0).toFixed(1)}
                    </span>
                  </div>
                </div>
                <div className="card-icon card-icon-broker2">
                  <WalletMinimal size={24} />
                </div>
              </div>
              <div className="card-summary-copy">
                <strong className={getKpiValueClass(displayProfits.broker2)}>
                  {displayProfits.broker2 !== 0
                    ? `$${displayProfits.broker2.toLocaleString()}`
                    : "$0"}
                </strong>
                <div className="card-detail-list">
                  <div className="card-detail-item">
                    <span className="card-detail-label">Balance</span>
                    <span className={getKpiDetailValueClass(Number(b2.balance || 0))}>
                      ${(b2.balance || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Equity</span>
                    <span className={getKpiDetailValueClass(Number(b2.equity || 0))}>
                      ${(b2.equity || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Margin</span>
                    <span className={getKpiDetailValueClass(Number(b2.margin || 0))}>
                      ${(b2.margin || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Free Margin</span>
                    <span className={getKpiDetailValueClass(Number(b2.freeMargin || 0))}>
                      ${(b2.freeMargin || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="performance-card pnl">
              <div className="card-top">
                <div className="card-heading-group">
                  <span className="card-title-label">Open P&amp;L</span>
                  <span className="card-subtitle">Premix active positions</span>
                </div>
                <div className="card-icon card-icon-profit">
                  <DollarSign size={24} />
                </div>
              </div>
              <div className="card-summary-copy">
                <strong className={getKpiValueClass(displayProfits.total)}>
                  {displayProfits.total !== 0 ? `$${displayProfits.total.toLocaleString()}` : "$0"}
                </strong>
                <div className="card-detail-list">
                  <div className="card-detail-item">
                    <span className="card-detail-label">Open Orders</span>
                    <span className={getKpiDetailValueClass(Number(totalOpenOrders || 0))}>
                      {totalOpenOrders}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Lots</span>
                    <span className={getKpiDetailValueClass(Number(totalOpenLots || 0))}>
                      {totalOpenLots.toFixed(2)}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Avg. Premium</span>
                    <span className={getKpiDetailValueClass(Number(avgPremium || 0))}>
                      {avgPremium.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="performance-card overall">
              <div className="card-top">
                <div className="card-heading-group">
                  <span className="card-title-label">Premix Summary</span>
                  <span className="card-subtitle">Summary with premium spread</span>
                </div>
                <div className="card-icon card-icon-overall">
                  <PiggyBank size={24} />
                </div>
              </div>
              <div className="card-summary-copy">
                <strong className={getKpiValueClass(overallPremixProfit)}>
                  {overallPremixProfit !== 0 ? `$${overallPremixProfit.toLocaleString()}` : "$0"}
                </strong>
                <div className="card-detail-list">
                  <div className="card-detail-item">
                    <span className="card-detail-label">Closed Trades</span>
                    <span className={getKpiDetailValueClass(Number(closedTradesTotalCount || 0))}>
                      {closedTradesTotalCount}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Buy Premium</span>
                    <span className={getKpiDetailValueClass(Number(buyPremium || 0), "negative")}>
                      {buyPremium.toFixed(2)}
                    </span>
                  </div>
                  <div className="card-detail-item">
                    <span className="card-detail-label">Sell Premium</span>
                    <span className={getKpiDetailValueClass(Number(sellPremium || 0), "positive")}>
                      {sellPremium.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {accountSets.length > 0 && (
            <div className="modern-selector-section">
              <div className="dashboard-selector-card-head">
                <div>
                  <div className="dashboard-selector-card-title">
                    <Activity size={16} />
                    <span>Market Setup</span>
                  </div>
                  <p>
                    Select the account set and symbols you want Premix to track from this workspace.
                  </p>
                </div>
                {currentSet && currentSet.symbolsLocked && displayQuoteSourceLabel && (
                  <div className="dashboard-selector-status-group">
                    <span className="dashboard-selector-pill">{displayQuoteSourceLabel}</span>
                    {quoteFeedStatus && (
                      <span className="dashboard-selector-pill dashboard-selector-pill-warning">
                        {quoteFeedStatus}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="dashboard-selector-grid">
                <div className="symbol-group">
                  <label>
                    <div className="dashboard-field-head">
                      <strong>Account Set</strong>
                      <span className="dashboard-field-head-meta">Choose configuration</span>
                    </div>
                    <CustomSelect
                      value={selectedSetId}
                      onChange={(value) => {
                        setSelectedSetId(value);
                        setStoredAccountSetId(value);
                      }}
                      options={accountSetOptions}
                      placeholder="Select account set"
                    />
                  </label>
                  <div className="quote-slot">
                    {currentSet && currentSet.symbolsLocked && (
                      <div
                        className="quote-display"
                        title={`Buy - Sell: ${(buyPremium - sellPremium).toFixed(2)}`}
                      >
                        <span className="bid">Buy Premium: {buyPremium.toFixed(2)}</span>
                        <span className="ask">Sell Premium: {sellPremium.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="symbol-group">
                  <label>
                    <div className="dashboard-field-head">
                      <strong>Future</strong>
                      <span
                        className={`dashboard-field-head-meta${symbolsLoading ? " dashboard-field-head-meta-loading" : ""}`}
                      >
                        {symbolsLoading ? (
                          <span
                            className="dashboard-inline-loader"
                            aria-label="Loading future symbols"
                          />
                        ) : currentSet?.requestedFutureSymbol && !currentSet?.symbolsLocked ? (
                          "via Link Account"
                        ) : (
                          `${broker1Symbols.length} symbols`
                        )}
                      </span>
                    </div>
                    <CustomSelect
                      value={futureSymbol}
                      onChange={handleFutureSelect}
                      options={futureOptions}
                      placeholder="Select future symbol"
                      disabled={
                        symbolsLoading ||
                        currentSet?.symbolsLocked ||
                        Boolean(currentSet?.requestedFutureSymbol)
                      }
                    />
                  </label>
                  <div className="quote-slot">
                    {visibleFutureQuote && (
                      <div className="quote-display">
                        <span className="bid">Bid: {visibleFutureQuote.bid?.toFixed(2)}</span>
                        <span className="ask">Ask: {visibleFutureQuote.ask?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="symbol-group">
                  <label>
                    <div className="dashboard-field-head">
                      <strong>Spot</strong>
                      <span
                        className={`dashboard-field-head-meta${symbolsLoading ? " dashboard-field-head-meta-loading" : ""}`}
                      >
                        {symbolsLoading ? (
                          <span
                            className="dashboard-inline-loader"
                            aria-label="Loading spot symbols"
                          />
                        ) : currentSet?.requestedSpotSymbol && !currentSet?.symbolsLocked ? (
                          "via Link Account"
                        ) : (
                          `${broker2Symbols.length} symbols`
                        )}
                      </span>
                    </div>
                    <CustomSelect
                      value={spotSymbol}
                      onChange={handleSpotSelect}
                      options={spotOptions}
                      placeholder="Select spot symbol"
                      disabled={
                        symbolsLoading ||
                        currentSet?.symbolsLocked ||
                        Boolean(currentSet?.requestedSpotSymbol)
                      }
                    />
                  </label>
                  <div className="quote-slot">
                    {visibleSpotQuote && (
                      <div className="quote-display">
                        <span className="bid">Bid: {visibleSpotQuote.bid?.toFixed(2)}</span>
                        <span className="ask">Ask: {visibleSpotQuote.ask?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dashboard-selector-footer">
                {lockGateMeta && (
                  <div
                    className={`dashboard-selector-gate dashboard-selector-gate-${lockGateMeta.tone}`}
                  >
                    <AlertTriangle size={15} />
                    <span>{lockGateMeta.message}</span>
                  </div>
                )}
                <div className="dashboard-selector-actions">
                  {currentSet && currentSet.symbolsLocked ? (
                    <button onClick={handleUnlockSymbols} className="unlock-btn">
                      <Unlock size={16} />
                      Unlock Symbols
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmitSymbols}
                      disabled={submitting || !futureSymbol || !spotSymbol || !canLockSymbols}
                      className="submit-btn"
                    >
                      {submitting ? "Submitting..." : "Lock Symbols"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentSet.symbolsLocked && selectedSetId ? (
            <div className="dashboard-chart-standalone">
              <div className="dashboard-chart-embed">
                <SellPremiumChart
                  timeframe={selectedTimeframe}
                  accountSetId={selectedSetId}
                  currentSet={currentSet}
                  activeTrades={activeTrades}
                  pendingOrders={pendingOrders}
                  debug={false}
                />
              </div>
            </div>
          ) : (
            <div className="dashboard-chart-standalone">
              <div className="chart-container">
                <div className="chart-empty-state">
                  <Lock className="chart-empty-icon" size={48} />
                  <h3 className="chart-empty-title">Chart Unavailable</h3>
                  <p className="chart-empty-copy">
                    Please lock symbols in the Account Set above
                    <br />
                    to start premium tracking and view the chart
                  </p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </DashboardContext.Provider>
  );
}
